import axios, { AxiosError } from 'axios';

axios.defaults.withCredentials = true;

/**
 * Type guard to check if error is an Axios error
 */
const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};

/**
 * Safely extracts error data from axios error
 */
const getErrorData = (error: unknown): unknown => {
  if (isAxiosError(error) && error.response?.data) {
    return error.response.data;
  }
  return error instanceof Error ? error.message : 'Unknown error';
};

/**
 * Checks if an HTTP status code represents an expected "not authenticated" response
 */
const isExpectedAuthFailure = (status: number): boolean => {
  return status === 401 || status === 403;
};

/**
 * Checks if the current user is authenticated by calling the auth endpoint
 *
 * Note: Returns false for both "user not authenticated" (expected) and
 * unexpected errors (network failures, server errors). Only unexpected errors are logged.
 *
 * @param authEndpoint - The base URL for the authentication API
 * @returns Promise that resolves to true if authenticated, false otherwise
 */
export const isAuthenticated = async (authEndpoint: string): Promise<boolean> => {
  try {
    await axios.post(`${authEndpoint}/me`);
    return true;
  } catch (err: unknown) {
    // Check if it's an axios error with a response
    if (isAxiosError(err) && err.response) {
      const status = err.response.status;

      // Expected: user is not authenticated (401/403) - this is normal, don't log
      if (isExpectedAuthFailure(status)) {
        return false;
      }

      // Unexpected: server error, bad gateway, etc. - log this
      if (process.env.NODE_ENV === 'development') {
        console.error('Unexpected auth error:', {
          status,
          message: err.message,
          data: err.response.data,
        });
      }
      return false;
    }

    // Network error or other unexpected error (no response)
    if (process.env.NODE_ENV === 'development') {
      console.error('Network/auth error:', err);
    }
    return false;
  }
};

/**
 * Signs out the current user
 * @param authEndpoint - The base URL for the authentication API
 * @param preSignOut - Optional callback function to execute before signing out
 * @returns Promise that resolves to true on success, false on failure
 */
export const signOut = async (
  authEndpoint: string,
  preSignOut?: () => void,
): Promise<boolean> => {
  try {
    if (preSignOut) {
      preSignOut();
    }
    await axios.post(`${authEndpoint}/signout`);
    return true;
  } catch (err: unknown) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error:', err);
    }
    return false;
  }
};

/**
 * Requests an email verification code to be sent to the user
 * @param authEndpoint - The base URL for the authentication API
 * @returns Promise that resolves to true on success
 * @throws The error response data from the server
 */
export const requestEmailVerificationCode = async (
  authEndpoint: string,
): Promise<boolean> => {
  try {
    await axios.post(`${authEndpoint}/verify`, {
      action: 'REQUEST_CODE',
    });
    return true;
  } catch (err: unknown) {
    const errorData = getErrorData(err);

    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error:', err);
    }

    throw errorData;
  }
};

/**
 * Confirms email verification using the provided code
 * @param code - The verification code sent to the user's email
 * @param authEndpoint - The base URL for the authentication API
 * @returns Promise that resolves to true on success
 * @throws The error response data from the server
 */
export const confirmEmailVerification = async (
  code: string,
  authEndpoint: string,
): Promise<boolean> => {
  try {
    await axios.post(`${authEndpoint}/verify`, {
      action: 'SUBMIT',
      code,
    });
    return true;
  } catch (err: unknown) {
    const errorData = getErrorData(err);

    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error:', err);
    }

    throw errorData;
  }
};

/**
 * Changes the user's password
 * @param currentPassword - The user's current password
 * @param newPassword - The new password to set
 * @param authEndpoint - The base URL for the authentication API
 * @returns Promise that resolves to true on success
 * @throws The error response data from the server
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  authEndpoint: string,
): Promise<boolean> => {
  try {
    await axios.post(`${authEndpoint}/manage-password`, {
      action: 'CHANGE',
      currentPassword,
      newPassword,
    });
    return true;
  } catch (err: unknown) {
    const errorData = getErrorData(err);

    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error:', err);
    }

    throw errorData;
  }
};
